import { useTranslation } from 'react-i18next'
import { Badge } from '@/components/ui/badge'
import { SEVERITY_META } from '@/features/schema/severity'
import type { ConceptSeverity } from '@/services/api/schemas'
import { cn } from '@/utils/cn'

export interface RowFindingBadgeProps {
  /** Worst severity landing on this row, or undefined when the row is clean. */
  severity?: ConceptSeverity
  className?: string
}

/**
 * Compact, icon-only severity badge shown on an entity/relation/pattern list row
 * after a "Validate schema" run, marking the row as the location of a finding
 * (slice 07 / SC-003). Renders nothing for a clean row. The severity label is
 * exposed via `aria-label`/`title` so the icon stays accessible.
 */
export function RowFindingBadge({ severity, className }: RowFindingBadgeProps) {
  const { t } = useTranslation()
  if (!severity) return null
  const meta = SEVERITY_META[severity]
  const label = t('schemaAgent.editor.findings.rowBadge', { severity: t(meta.labelKey) })
  const Icon = meta.Icon
  return (
    <Badge
      className={cn(meta.badgeClass, 'h-5 w-5 shrink-0 justify-center p-0', className)}
      role="img"
      aria-label={label}
      title={label}
    >
      <Icon className="h-3 w-3" aria-hidden />
    </Badge>
  )
}
